import React from "react";
import { ContentBlock } from "@/types/case-study";
import { Quote, GripVertical, Minus } from "lucide-react";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { cleanWavyUnderlines } from "@/utils/cleanWavyUnderlines";

interface QuoteBlockProps {
  block: ContentBlock;
  onUpdate: (content: any) => void;
  onRemove?: () => void;
  isActive?: boolean;
  isRemovable?: boolean;
}

export const QuoteBlock: React.FC<QuoteBlockProps> = ({
  block,
  onUpdate,
  onRemove,
  isActive = false,
  isRemovable = true,
}) => {
  const handleTextChange = (text: string) => {
    onUpdate({
      ...block.content,
      text,
    });
  };

  const handleAuthorChange = (author: string) => {
    onUpdate({
      ...block.content,
      author,
    });
  };

  const text = block.content?.text || "";
  const author = block.content?.author || "";

  return (
    <div className={`group relative ${isActive ? '' : ''}`}>
      {isActive ? (
        // Edit Mode - Card with editor
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="p-4 space-y-4">
            {/* Author Name Input */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-900">
                Author Name
              </label>
              <input
                type="text"
                value={author}
                onChange={(e) => handleAuthorChange(e.target.value)}
                placeholder="Author Name"
                className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Quote Text Editor */}
            <div className="">
              <div className="min-h-[160px] overflow-hidden">
                <RichTextEditor
                  value={text}
                  onChange={handleTextChange}
                  placeholder="Add your Quote Here......"
                  maxLength={2000}
                />
              </div>
            </div>
          </div>

          {isRemovable && onRemove && (
            <button
              onClick={onRemove}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-70 hover:opacity-100 transition-opacity hover:bg-red-600"
              title="Delete this content block"
            >
              ×
            </button>
          )}
        </div>
      ) : (
        // Preview Mode - Clean minimalist display
        <div className="text-center py-8">
          <div className="max-w-4xl mx-auto">
            <blockquote className="text-xl md:text-2xl italic font-serif text-gray-800 leading-relaxed mb-4">
              {text ? (
                <div dangerouslySetInnerHTML={{ __html: cleanWavyUnderlines(text) }} />
              ) : (
                <span className="text-gray-400">Enter your quote here...</span>
              )}
            </blockquote>
            {author && (
              <div className="text-center">
                <cite className="text-sm font-bold text-gray-800 not-italic">
                  — {author}
                </cite>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

